import { CartHeaderProps } from '@msc-b2c/interfaces/types';
import { Button, Text } from '@msc-b2c/shared-components';
import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './cartheader.module.scss';
import {
  formatPriceWithoutRounding,
  getFormatPriceCurrencyOrder,
  managePlurals,
  mscGet,
  replacePlaceholders,
  RootState,
} from '@msc-b2c/shared-utils';
import { useSelector } from 'react-redux';
import { navigateOrRedirectToRoute } from '../../../navigationmenu/utils/navigationMenuUtils';
import { getMergedCartDisplayItems } from '../../../utils/myAreaCartUtils';
import { getContainerInfo } from '../../../utils/analyticsUtils';
import { triggerClickCtaEvent } from '../../../utils/myAreaAnalytics';
import {
  CART_FROM_CHECKOUT_AVAILABILITY_REDIRECT_STATE,
  CartNavigationState,
} from '../../utils/cartAvailabilityUtils';

type CartHeaderWithBannerProps = CartHeaderProps & {
  bannerContent?: ReactNode;
};

export function CartHeader(props: Readonly<CartHeaderWithBannerProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartLink } = useSelector((state: RootState) => state.navigationMenuData);
  const { prices } = useSelector((state: RootState) => state.myAreaCart);
  const showItemsCount = props.showItemsCount ?? true;
  const backTarget = props.backTarget ?? 'previous';
  const displayItemCount = getMergedCartDisplayItems(props.items).length;
  const hasItems = props.items.length > 0;
  const formattedTotalPrice = getFormatPriceCurrencyOrder(
    formatPriceWithoutRounding(String(prices?.price?.total?.afterDiscount || 0))
  );

  const handleBackClick = () => {
    const ctaDestinationLink =
      backTarget === 'cart' ? cartLink?.url : mscGet<string>('session', 'started_at');

    triggerClickCtaEvent({
      cta_dest_link: ctaDestinationLink ?? '',
      click_main_name: props.backButtonText,
      container_info: getContainerInfo(document.querySelector(`.${styles['cmp-cartheader']}`)),
      element_position: 'cart header',
      element_type: 'button',
      click_location: 'fixed',
    });

    if (backTarget === 'cart') {
      navigateOrRedirectToRoute(cartLink || {}, navigate, { replace: true });
      return;
    }

    const fromCheckoutAvailabilityRedirect = (location.state as CartNavigationState | null)?.[
      CART_FROM_CHECKOUT_AVAILABILITY_REDIRECT_STATE
    ];

    // Cart→checkout uses replace, so availability redirect lands on [previous, cart]
    // (not [previous, cart, cart]). One step returns to the page before cart.
    if (fromCheckoutAvailabilityRedirect) {
      navigate(-1);
      return;
    }

    navigate(-1);
  };

  return (
    <div className={styles['cmp-cartheader']}>
      <Button
        icon="left"
        styleOption={`cmp-button__link spacer-pt-016 spacer-pb-016`}
        text={props.backButtonText}
        accessibilityLabel={props.backButtonText}
        onClick={handleBackClick}
      />

      {props.bannerContent && (
        <div className={styles['cmp-cartheader__banner']}>{props.bannerContent}</div>
      )}

      <div
        className={`${styles['cmp-cartheader__content']} ${
          hasItems
            ? styles['cmp-cartheader__content--with-items']
            : styles['cmp-cartheader__content--empty']
        } spacer-md-mt-048 spacer-mb-016 spacer-md-mb-024`}
      >
        <Text
          tagHtml={'p'}
          text={props.title}
          styleOption={`text header-m`}
        />
        {showItemsCount && (
          <div className={styles['cmp-cartheader__content-count-wrapper']}>
            <Text
              tagHtml={'p'}
              text={managePlurals(
                displayItemCount,
                replacePlaceholders(props.itemsCount, {
                  count: displayItemCount.toString(),
                })
              )}
              styleOption={`${styles['cmp-cartheader__content-count']} text body`}
            />
            {hasItems && (
              <Text
                tagHtml={'p'}
                text={formattedTotalPrice}
                styleOption={`${styles['cmp-cartheader__content-total']} text body`}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
